// 📄 pages/Admin/sections/InterfaceCatalogSection.tsx
import { useEffect, useRef, useState } from 'react';
import { Plus, RefreshCw, ArrowLeftRight, Sparkles } from 'lucide-react';
import { InterfaceChannel, getInterfaces, deleteInterface, seedCatalogoInterfaces } from '../../../services/catalogoInterfacesService.ts';
import { useAuth } from '../../../context/AuthContext.tsx';
import InterfaceCatalogGrid from '../../../components/interfaces/InterfaceCatalogGrid.tsx';
import InterfaceFormModal from '../components/InterfaceFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

interface InterfaceCatalogSectionProps {
  // Ir al Catálogo de Transacciones — acá el "acá" del comentario de
  // InterfaceCatalogGrid.tsx es cambiar de sección dentro del mismo panel
  // Admin (ver AdminPanel.tsx, que la pasa). Opcional para no romper si
  // alguna vez se monta esta sección fuera de ese contexto.
  onIrACatalogoTransacciones?: () => void;
}

export default function InterfaceCatalogSection({ onIrACatalogoTransacciones }: InterfaceCatalogSectionProps) {
  const { user } = useAuth();
  const [interfaces, setInterfaces] = useState<InterfaceChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InterfaceChannel | null>(null);
  const [itemAEliminar, setItemAEliminar] = useState<InterfaceChannel | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El Admin ya tiene permiso de escritura garantizado (ruta gateada por
  // AdminRoute), así que acá el auto-seed es siempre seguro. Ver mismo
  // criterio en [[TransactionCatalogSection.tsx]].
  const seedIntentado = useRef(false);

  const cargar = async () => {
    setLoading(true);
    try {
      let data = await getInterfaces();
      if (data.length === 0 && !seedIntentado.current) {
        seedIntentado.current = true;
        setSembrando(true);
        try {
          await seedCatalogoInterfaces(user?.email || user?.uid || '');
          data = await getInterfaces();
        } finally {
          setSembrando(false);
        }
      }
      setInterfaces(data);
    } catch (err) {
      console.error('Error cargando el catálogo de interfaces/canales:', err);
      setError('No se pudo cargar el catálogo de interfaces/canales.');
    } finally {
      setLoading(false);
    }
  };

  // Solo al montar: AdminRoute ya garantiza que `user` está resuelto antes
  // de que esta sección exista (gatea la ruta completa hasta que termine
  // de cargar la sesión), así que no hay riesgo real de que `cargar` quede
  // con un `user` obsoleto por no re-suscribirse a sus cambios.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const openCreate = () => { setEditingItem(null); setModalOpen(true); };
  const openEdit = (item: InterfaceChannel) => { setEditingItem(item); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!itemAEliminar) return;
    setEliminando(true);
    try {
      await deleteInterface(itemAEliminar.id);
      setItemAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar la interfaz/canal:', err);
    } finally {
      setEliminando(false);
    }
  };

  const handleSeed = async () => {
    setSembrando(true);
    setError(null);
    try {
      await seedCatalogoInterfaces(user?.email || user?.uid || '');
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al cargar los datos de ejemplo.');
    } finally {
      setSembrando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Catálogo de Interfaces / Canales</h1>
          <p className="text-sm text-gray-400 mt-1">Interfaces de integración SAP Ariba ↔ ERP ↔ S/4HANA ↔ BTP: por dónde entra/sale cada flujo y en qué transacción se monitorea.</p>
        </div>
        <div className="flex items-center gap-2">
          {interfaces.length === 0 && !loading && (
            <button
              onClick={handleSeed}
              disabled={sembrando}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-50 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-300 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              {sembrando ? 'Cargando...' : 'Cargar datos de ejemplo'}
            </button>
          )}
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
          >
            <Plus className="w-4 h-4" />
            Nueva Interfaz
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
        </div>
      ) : interfaces.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no hay interfaces/canales registrados en el catálogo.</p>
        </div>
      ) : (
        <InterfaceCatalogGrid
          interfaces={interfaces}
          onEdit={openEdit}
          onDelete={setItemAEliminar}
          onVerEnTransacciones={onIrACatalogoTransacciones}
        />
      )}

      {modalOpen && (
        <InterfaceFormModal item={editingItem} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}

      {itemAEliminar && (
        <ConfirmDialog
          title="¿Eliminar esta interfaz/canal del catálogo?"
          message={`"${itemAEliminar.nombreTecnico} — ${itemAEliminar.textoBreve}" se eliminará del catálogo global. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setItemAEliminar(null)}
        />
      )}
    </div>
  );
}
